import React, { FC, useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  adminActions,
  adminGetAllLeads,
  allLeadsSelector,
  getBroadcastGroupsForDropdown,
  HelpTypes,
  IAdminLeadsParams,
  isLoadingSelector,
  LeadStatusType,
  resetStatus,
  successOrSelector,
  successSelector,
} from '@pharmaplan/common';
import { getLeadsFilter } from '@pharmaplan/common/actions/adminActions';

import { showSuccess } from '../Profile/Pharmacist/PharmacistViewProfile/helper';
import useUserProfileLink from '../../../hooks/Admin/useUserProfileLink';
import { IDynamicTableObject } from '../../DynamicTable/types';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import useSelectedTabs from '../../../hooks/Admin/useSelectedTabs';
import useLeads from '../../../hooks/Admin/Leads/useLeads';
import { setDialog } from '../../../reducers/dialogReducer';
import useTableSort from '../../../hooks/useTableSort';
import { Constants } from '../../../helpers/Constants';
import DynamicTable from '../../DynamicTable';
import strings from '../../../localization';

import UploadLeadsModal from './UploadLeadsModal/UploadLeadsModal';
import { leadHeaders, leadRows } from './helper';
import LeadsHeader from './LeadsHeader';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';

const loadingActions = [adminActions.getAllLeads];

export enum LeadTabs {
  pharmacy = '10',
  pharmacist = '11',
  technicians = '14',
}

interface ILeads {
  defaultTab?: LeadTabs;
}

const Leads: FC<ILeads> = ({ defaultTab }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { can } = useAdminPermissions();

  const { state: locState } = useLocation();
  const { activeTab, campaignId } = locState ?? {};

  const list = useAppSelector(allLeadsSelector);
  const { selectedTabs, handleTabClick } = useSelectedTabs<LeadTabs>(defaultTab ?? activeTab);
  const typedSelectedTab = selectedTabs as unknown as HelpTypes;

  const inviteSuccess = useAppSelector((state) =>
    successOrSelector(
      [adminActions.inviteLeads, adminActions.adminCreateEntitlment],
      state,
    ));

  const deletedSuccess = useAppSelector((state) =>
    successSelector([adminActions.deleteLead], state));

  const isLoading = useAppSelector((state) =>
    isLoadingSelector(loadingActions, state));

  const { goToUserProfile } = useUserProfileLink();
  const { isDesc, orderBy, order, handleSorting, resetSorting } = useTableSort();

  const [page, setPage] = useState(1);

  const fetchLeads = (values: FormikValues, targetPage: number) => {
    const params: IAdminLeadsParams = {
      page: targetPage,
      leadName: values.leadName || undefined,
      leadEmail: values.leadEmail || undefined,
      campaignId: values.campaignId || undefined,
      broadcastGroupId: values.broadcastGroupId || undefined,
      status:
        values.status === '' ? '' : (Number(values.status) as LeadStatusType),
      userType: typedSelectedTab,
      sortBy: orderBy,
      desc: isDesc,
    };

    dispatch(adminGetAllLeads(params));
  };

  const handleSubmit = (values: FormikValues) => {
    setPage(1);
    fetchLeads(values, 1);
  };

  const formik = useFormik({
    initialValues: {
      lead: '',
      campaignId: campaignId || '',
      broadcastGroupId: '',
      status: '',
      selectAllType: [],
    },
    onSubmit: handleSubmit,
  });

  const {
    handleCreate,
    handleDelete,
    handleEdit,
    handlePress,
    handleReset,
    selectedIds,
    handleInviteSelected,
    applySelectAllTypes,
    sendInvitationToLeads,
    handleAddCredits,
    handleDetails,
    handleCampaignDetails,
  } = useLeads(page, formik, selectedTabs);

  const refreshLeads = () => {
    fetchLeads(formik.values, 1);
    setPage(1);
  };

  const handleUploadLeads = () => {
    dispatch(
      setDialog({
        heading: { title: strings.uploadLeads },
        showCloseButton: true,
        maxWidth: 'sm',
        Component: (
          <UploadLeadsModal
            userType={typedSelectedTab}
            onSuccess={refreshLeads}
          />
        ),
      }),
    );
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const targetPage = selectedPage + 1;
    setPage(targetPage);
    fetchLeads(formik.values, targetPage);
  };

  const reset = () => {
    setPage(1);
    handleReset();
    resetSorting();
    formik.resetForm();
    formik.setFieldValue('campaignId', '');
    formik.setFieldValue('broadcastGroupId', '');
    dispatch(adminGetAllLeads({ page: 1, userType: typedSelectedTab }));
  };

  const handleViewBookings = (leadId: string) =>
    () => {
      navigate(Constants.paths.admin.leadBookings, { state: { leadId } });
    };

  useEffect(() => {
    handleTabClick(defaultTab ?? activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTab]);

  useEffect(() => {
    dispatch(getBroadcastGroupsForDropdown());
  }, [dispatch]);

  const table: IDynamicTableObject = {
    headers: leadHeaders(),
    rows: leadRows({
      can,
      data: list?.data,
      selectedItems: selectedIds,
      handlePress,
      handleEdit,
      handleDelete,
      handleViewBookings,
      sendInvite: sendInvitationToLeads,
      handleDetails,
      goToUserProfile,
      handleAddCredits,
      selectAllType: formik.values?.selectAllType ?? [],
      userType: typedSelectedTab,
      handleCampaignDetails,
    }),
    headerBar: [],
  };

  useEffect(() => {
    dispatch(
      adminGetAllLeads({
        page: 1,
        userType: typedSelectedTab,
        campaignId,
      }),
    );

    formik.resetForm();

    dispatch(
      getLeadsFilter({
        userType: typedSelectedTab,
        dontShowExpired: !campaignId,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTabs]);

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      fetchLeads(formik.values, 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  useEffect(() => {
    if (deletedSuccess) {
      showSuccess(dispatch, strings.deletedSuccessfully);
      dispatch(resetStatus([adminActions.deleteLead]));
      fetchLeads(formik.values, 1);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deletedSuccess]);

  useEffect(() => {
    if (inviteSuccess) {
      showSuccess(dispatch, strings.actionCompletedSuccessfully);
      dispatch(
        resetStatus([
          adminActions.inviteLeads,
          adminActions.adminCreateEntitlment,
        ]),
      );
      fetchLeads(formik.values, 1);
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteSuccess]);

  return (
    <DynamicTable
      table={table}
      showHeader={false}
      loadSuccess={!isLoading}
      page={page}
      totalCount={list?.totalCount}
      orderBy={orderBy}
      order={order}
      handleSort={handleSorting}
      handlePagination={handlePagination}
      customHeader={(
        <LeadsHeader
          formik={formik}
          disabled={!selectedIds.length}
          handleReset={reset}
          selectedTabs={selectedTabs}
          handleCreate={handleCreate}
          handleTabClick={handleTabClick}
          handleUploadLeads={handleUploadLeads}
          handleInviteSelected={handleInviteSelected}
          handleSelectAllTypeChange={applySelectAllTypes}
        />
      )}
    />
  );
};

export default Leads;
